/**
 * Coordinate Transformation Utility
 * 
 * Converts coordinates from the 1.6:1 aspect ratio container space
 * to the actual image coordinate space.
 * 
 * This is essential because the card preview uses a fixed 1.6:1 container
 * with object-contain, so template coordinates are relative to that container,
 * not to the actual image dimensions.
 */

const CONTAINER_ASPECT = 1.6;

export interface CoordinateTransform {
  toImageX: (containerX: number) => number;
  toImageY: (containerY: number) => number;
  toImageW: (containerW: number) => number;
  toImageH: (containerH: number) => number;
}

/**
 * Creates a coordinate transformer for converting container-space coordinates
 * to image-space coordinates.
 */
export function getCoordinateTransform(
  imageWidth: number,
  imageHeight: number
): CoordinateTransform {
  const imageAspect = imageWidth / imageHeight;
  
  let imageInContainerX = 0, imageInContainerY = 0;
  let imageInContainerW = 100, imageInContainerH = 100;
  
  if (imageAspect > CONTAINER_ASPECT) {
    // Image is wider than container - letterboxed top/bottom
    imageInContainerH = (CONTAINER_ASPECT / imageAspect) * 100;
    imageInContainerY = (100 - imageInContainerH) / 2;
  } else if (imageAspect < CONTAINER_ASPECT) {
    // Image is taller than container - pillarboxed left/right
    imageInContainerW = (imageAspect / CONTAINER_ASPECT) * 100;
    imageInContainerX = (100 - imageInContainerW) / 2;
  }
  
  return {
    toImageX: (containerX) => ((containerX - imageInContainerX) / imageInContainerW) * 100,
    toImageY: (containerY) => ((containerY - imageInContainerY) / imageInContainerH) * 100,
    toImageW: (containerW) => (containerW / imageInContainerW) * 100,
    toImageH: (containerH) => (containerH / imageInContainerH) * 100,
  };
}

/**
 * Calculate correct photo aspect ratio using coordinate transformation.
 * This ensures the photo crop matches the actual frame on the card.
 */
export function calculatePhotoAspect(
  imageWidth: number,
  imageHeight: number,
  photoPosition: { width: number; height: number }
): number | undefined {
  const transform = getCoordinateTransform(imageWidth, imageHeight);
  
  // Transform container-space percentages to image-space percentages
  const frameWidthPercent = transform.toImageW(photoPosition.width);
  const frameHeightPercent = transform.toImageH(photoPosition.height);
  
  // Convert to actual pixel dimensions
  const frameWidth = (frameWidthPercent / 100) * imageWidth;
  const frameHeight = (frameHeightPercent / 100) * imageHeight;
  
  if (frameHeight === 0) return undefined;
  return frameWidth / frameHeight;
}

/**
 * Calculate correct photo output size using coordinate transformation.
 * Returns dimensions with quality multiplier for high-resolution output.
 */
export function calculatePhotoOutputSize(
  imageWidth: number,
  imageHeight: number,
  photoPosition: { width: number; height: number },
  qualityMultiplier: number = 2
): { width: number; height: number } | undefined {
  const transform = getCoordinateTransform(imageWidth, imageHeight);
  
  // Transform container-space percentages to image-space percentages
  const frameWidthPercent = transform.toImageW(photoPosition.width);
  const frameHeightPercent = transform.toImageH(photoPosition.height);
  
  // Convert to actual pixel dimensions
  const frameWidthRaw = (frameWidthPercent / 100) * imageWidth;
  const frameHeightRaw = (frameHeightPercent / 100) * imageHeight;
  
  if (frameHeightRaw === 0) return undefined;
  
  // Apply quality multiplier for high-resolution output
  const width = Math.max(1, Math.round(frameWidthRaw * qualityMultiplier));
  const height = Math.max(1, Math.round(frameHeightRaw * qualityMultiplier));
  
  return { width, height };
}
